import { Follow, User } from '@app/models';
import { FollowGroup } from '@app/models/follow-group';
import {
  PackagingManagerActionTypes,
  PackagingManagerActionsUnion
} from '@core/actions/packaging-manager.actions';
import { createFeatureSelector, createSelector } from '@ngrx/store';

export interface FieldMeta {
  productName: string;
  fieldName: string;
}

export interface ProductBreakdownItem {
  id: number;
  code: string;
  description: string;
  businessUnit?: string;
  countryNames?: string;
  values?: { [key: string]: any };
}

export interface SectionBreakdown {
  label: string;
  total: number;
  withInfo: number;
  fields: FieldMeta[];
  products: ProductBreakdownItem[];
}

export interface Packaging {
  id: number;
  packagingName: string;
  followGroupsId: number;
  followId: number;
  userId: number;
  Follow: Follow;
  FollowGroup: FollowGroup;
  User: User;
  MilestoneId: number;
  status: number;
  parentId: number;
  archivedStatus: number;
  createdAt: Date;
  updatedAt: Date;
  deletedAt: Date;
  totalProducts?: number;
  productBreakdown?: { [typeOfField: string]: SectionBreakdown };
}

export interface State {
  unassigned: any[];
  assigned: any[];
  packagingUsers: any[];
  pendingCount: number;
  assigning: boolean;
  loaded: boolean;
}

export const INITIAL_STATE: State = {
  unassigned: [],
  assigned: [],
  packagingUsers: [],
  pendingCount: 0,
  assigning: false,
  loaded: false
};

export function reducer(
  state: State = INITIAL_STATE,
  action: PackagingManagerActionsUnion
) {
  switch (action.type) {
    case PackagingManagerActionTypes.LoadUnassigned:
      return { ...state, pendingCount: state.pendingCount + 1 };

    case PackagingManagerActionTypes.LoadUnassignedFail:
      return { ...state, pendingCount: state.pendingCount - 1 };

    case PackagingManagerActionTypes.LoadUnassignedSuccess:
      return {
        ...state,
        unassigned: action.packagingManagers,
        pendingCount: state.pendingCount - 1,
        loaded: true
      };

    case PackagingManagerActionTypes.AddAllUnassigned:
      return {
        ...state,
        unassigned: action.packagingManagers
      };

    case PackagingManagerActionTypes.LoadAssigned:
      return { ...state, pendingCount: state.pendingCount + 1 };

    case PackagingManagerActionTypes.LoadAssignedFail:
      return { ...state, pendingCount: state.pendingCount - 1 };

    case PackagingManagerActionTypes.LoadAssignedSuccess:
      return {
        ...state,
        assigned: action.packagingManagers,
        pendingCount: state.pendingCount - 1
      };

    case PackagingManagerActionTypes.AddAllAssigned:
      return {
        ...state,
        assigned: action.packagingManagers
      };

    case PackagingManagerActionTypes.LoadPackagingUsers:
      return { ...state, pendingCount: state.pendingCount + 1 };

    case PackagingManagerActionTypes.LoadPackagingUsersFail:
      return { ...state, pendingCount: state.pendingCount - 1 };

    case PackagingManagerActionTypes.LoadPackagingUsersSuccess:
      return {
        ...state,
        packagingUsers: action.packagingUsers,
        pendingCount: state.pendingCount - 1
      };

    case PackagingManagerActionTypes.AddAllPackagingUsers:
      return { ...state, packagingUsers: action.packagingUsers };

    case PackagingManagerActionTypes.AssignUser:
      return { ...state, assigning: true };

    case PackagingManagerActionTypes.AssignUserFail:
      return { ...state, assigning: false };

    case PackagingManagerActionTypes.AssignUserSuccess:
      return {
        ...state,
        unassigned: state.unassigned.filter(
          packaging => packaging.id !== action.packaging.id
        ),
        assigned: state.assigned.some(
          packaging => packaging.id === action.packaging.id
        )
          ? state.assigned.map(packaging =>
              packaging.id === action.packaging.id
                ? action.packaging
                : packaging
            )
          : [...state.assigned, action.packaging],
        assigning: false
      };

    case PackagingManagerActionTypes.UnassignUser:
      return { ...state, assigning: true };

    case PackagingManagerActionTypes.UnassignUserFail:
      return { ...state, assigning: false };

    case PackagingManagerActionTypes.UnassignUserSuccess: {
      const target = state.assigned.find(
        packaging => packaging.id === action.packagingId
      );
      const becameUnassigned =
        target &&
        (target.UserPackaging || []).length === 1 &&
        target.UserPackaging[0].userId === action.userId;

      const assigned = state.assigned
        .map(packaging => {
          if (packaging.id !== action.packagingId) {
            return packaging;
          }
          return {
            ...packaging,
            UserPackaging: (packaging.UserPackaging || []).filter(
              assignment => assignment.userId !== action.userId
            )
          };
        })
        .filter(
          packaging =>
            !(packaging.id === action.packagingId && becameUnassigned)
        );

      const unassigned = becameUnassigned
        ? [...state.unassigned, { ...target, UserPackaging: [] }]
        : state.unassigned;

      return { ...state, assigned, unassigned, assigning: false };
    }

    default:
      return state;
  }
}

export const getState = createFeatureSelector<State>('packagingManagers');

export const getUnassigned = createSelector(
  getState,
  (state: State) => state.unassigned
);

export const getAssigned = createSelector(
  getState,
  (state: State) => state.assigned
);

export const getPackagingUsers = createSelector(
  getState,
  (state: State) => state.packagingUsers
);

export const getIsLoading = createSelector(
  getState,
  (state: State) => state.pendingCount > 0
);

// export const getIsAssigning = (state: State) => state.assigning;
